/**
 * Landing Page
 * Apple-inspired public homepage for 1crew3
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { PAGE_SEO } from '../utils/seoConstants';
import { generateWebSiteSchema } from '../utils/schemaGenerators';
import {
  Anchor,
  Ship,
  Users,
  MapPin,
  Star,
  Shield,
  Clock,
  MessageCircle,
  ArrowRight,
  CheckCircle,
  Briefcase,
  Zap,
  Check,
} from 'lucide-react';

const FREE_PERIOD_END = process.env.REACT_APP_FREE_PERIOD_END || '2026-06-01T00:00:00Z';

function useCountdown() {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  function calculateTimeLeft() {
    const endDate = new Date(FREE_PERIOD_END).getTime();
    const now = Date.now();
    const difference = endDate - now;

    if (difference <= 0) {
      return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
      expired: false,
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000)
    };
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return timeLeft;
}

function LandingPage() {
  const timeLeft = useCountdown();
  const isFreePeriod = !timeLeft.expired;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={PAGE_SEO.home.title}
        description={PAGE_SEO.home.description}
        canonicalUrl="/"
        schema={generateWebSiteSchema()}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-navy-900"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center">
              <img src="/logo.png" alt="1crew3" className="h-10 w-10 rounded-lg" />
              <span className="ml-2.5 text-lg font-bold text-white tracking-tight">
                1crew3
              </span>
            </div>
            <div className="flex items-center space-x-6">
              <a
                href="/blog"
                className="text-sm text-gray-300 hover:text-white transition-colors duration-300"
              >
                Blog
              </a>
              <Link
                to="/login"
                className="text-sm text-gray-300 hover:text-white transition-colors duration-300"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="text-sm bg-gold-500 text-navy-900 hover:bg-gold-400 py-1.5 px-4 rounded-full font-medium transition-all duration-300"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 lg:pt-44 lg:pb-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex flex-col items-center justify-center mb-8">
            <img src="/logo.png" alt="1crew3" className="h-64 w-64 sm:h-72 sm:w-72 rounded-3xl shadow-lg" />
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-navy-900 leading-[1.05] tracking-tight">
            Connect. Match. <span className="text-primary-600">Work.</span>
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-gold-500 italic font-light tracking-wide">
            Your gateway to the yachting industry.
          </p>
          <p className="mt-6 text-xl sm:text-2xl text-navy-500 max-w-2xl mx-auto font-light leading-relaxed">
            Get mooring help or find the perfect crew.
            Whether you're seeking your next adventure or building your team,
            1crew3 connects you.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register?type=owner"
              className="btn-primary text-base px-8 py-3.5"
            >
              Register as Owner
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/register?type=helper"
              className="btn-secondary text-base px-8 py-3.5"
            >
              Register as Helper/Crew
            </Link>
          </div>
        </div>
      </section>

      {/* Free Registration & Pricing Section */}
      <section className="py-16 bg-gradient-to-b from-primary-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Free for Crew Banner */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <CheckCircle className="h-4 w-4" />
              Crew & Helpers: Always FREE
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 tracking-tight">
              Simple, Transparent Pricing
            </h2>
            <p className="mt-4 text-lg text-navy-500 font-light max-w-2xl mx-auto">
              Free for crew members forever. Yacht owners get free access until June 1st, 2026.
            </p>
          </div>

          {/* Countdown Timer for Owners */}
          {isFreePeriod && (
            <div className="bg-white rounded-2xl shadow-lg border border-navy-100 p-8 mb-12 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 rounded-full mb-4">
                  <Clock className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-navy-900 mb-2">
                  Yacht Owners: Free Until June 1st, 2026
                </h3>
                <p className="text-navy-500 text-sm mb-6">
                  Register now and use all features free during the launch period
                </p>
                <div className="flex justify-center gap-3">
                  {[
                    { value: timeLeft.days, label: 'Days' },
                    { value: timeLeft.hours, label: 'Hours' },
                    { value: timeLeft.minutes, label: 'Min' },
                    { value: timeLeft.seconds, label: 'Sec' },
                  ].map((unit, i) => (
                    <div key={i} className="flex flex-col items-center">
                      <div className="bg-navy-900 text-white rounded-lg px-4 py-3 min-w-[60px]">
                        <span className="text-2xl font-bold">
                          {String(unit.value).padStart(2, '0')}
                        </span>
                      </div>
                      <span className="text-xs text-navy-500 mt-1 font-medium">{unit.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* Crew - Always Free */}
            <div className="bg-white rounded-2xl border-2 border-green-200 p-6 text-center">
              <div className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                <Users className="h-3 w-3" />
                CREW & HELPERS
              </div>
              <div className="text-3xl font-bold text-navy-900 mb-1">Free</div>
              <div className="text-navy-500 text-sm mb-6">Forever</div>
              <ul className="space-y-3 text-left mb-6">
                {['Create your profile', 'Apply to positions', 'Direct messaging', 'Build your reputation'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-navy-700">
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link to="/register?type=helper" className="block w-full py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors">
                Join Free
              </Link>
            </div>

            {/* Owner Monthly */}
            <div className="bg-white rounded-2xl border-2 border-navy-200 p-6 text-center">
              <div className="inline-flex items-center gap-1 bg-navy-100 text-navy-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                <Ship className="h-3 w-3" />
                YACHT OWNERS
              </div>
              <div className="text-3xl font-bold text-navy-900 mb-1">€29<span className="text-lg font-normal text-navy-500">/mo</span></div>
              <div className="text-navy-500 text-sm mb-6">After free period</div>
              <ul className="space-y-3 text-left mb-6">
                {['Unlimited requests', 'Access all crew', 'Real-time chat', 'Ratings & reviews'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-navy-700">
                    <Check className="h-4 w-4 text-primary-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link to="/register?type=owner" className="block w-full py-2.5 bg-navy-100 text-navy-700 rounded-lg font-medium hover:bg-navy-200 transition-colors">
                Start Free Trial
              </Link>
            </div>

            {/* Owner Yearly - Best Value */}
            <div className="bg-white rounded-2xl border-2 border-primary-500 p-6 text-center relative shadow-lg">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="inline-flex items-center gap-1 bg-primary-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  <Zap className="h-3 w-3" />
                  BEST VALUE
                </span>
              </div>
              <div className="inline-flex items-center gap-1 bg-primary-50 text-primary-700 text-xs font-semibold px-3 py-1 rounded-full mb-4 mt-2">
                <Ship className="h-3 w-3" />
                YACHT OWNERS
              </div>
              <div className="text-3xl font-bold text-navy-900 mb-1">€249<span className="text-lg font-normal text-navy-500">/yr</span></div>
              <div className="text-green-600 text-sm font-medium mb-6">Save 28% (2 months free)</div>
              <ul className="space-y-3 text-left mb-6">
                {['Everything in Monthly', 'Priority support', 'Best value', 'Cancel anytime'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-navy-700">
                    <Check className="h-4 w-4 text-primary-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link to="/register?type=owner" className="block w-full py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>

          <p className="text-center text-navy-400 text-sm mt-8">
            No credit card required during free period. Secure payments powered by Stripe.
          </p>
        </div>
      </section>

      {/* Why 1crew3 - Features */}
      <section className="py-20 bg-navy-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 tracking-tight">
              Why 1crew3?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: 'Qualified Crew',
                description:
                  'Easily browse through local candidates matching your exact needs and requirements.',
              },
              {
                icon: Briefcase,
                title: 'Crew Agency & Mooring Help',
                description:
                  'Find qualified crew for positions or get instant mooring assistance at any port.',
              },
              {
                icon: Clock,
                title: 'Real-Time & Scheduled',
                description:
                  'Plan ahead or request help instantly. We adapt to your schedule.',
              },
              {
                icon: Star,
                title: 'Trusted Reviews',
                description:
                  'Make informed decisions with our two-way rating and review system.',
              },
              {
                icon: MessageCircle,
                title: 'In-App Chat',
                description:
                  'Coordinate seamlessly with secure, direct messaging.',
              },
              {
                icon: MapPin,
                title: 'Global Coverage',
                description:
                  'Find crew and helpers across the Mediterranean, Caribbean, and beyond.',
              },
            ].map((feature, index) => (
              <div key={index} className="card">
                <feature.icon className="h-8 w-8 text-primary-600 mb-4" />
                <h3 className="text-lg font-semibold text-navy-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-navy-500 font-light leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 tracking-tight">
              How It Works
            </h2>
            <p className="mt-4 text-lg text-navy-500 font-light">
              Getting crew or help at the dock has never been easier
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                step: '1',
                title: 'Post Your Request',
                description:
                  'Tell us what you need — mooring help or crew positions. Add details about your yacht and requirements.',
                icon: MapPin,
              },
              {
                step: '2',
                title: 'Choose Your Crew/Helper',
                description:
                  'Browse crew in your area, check their ratings, experience and certifications, then select the best match.',
                icon: Users,
              },
              {
                step: '3',
                title: 'Connect & Work Together',
                description:
                  'Chat directly, coordinate details, and enjoy smooth mooring or find your perfect crew member for the season.',
                icon: Ship,
              },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="h-16 w-16 bg-navy-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <item.icon className="h-7 w-7 text-primary-600" />
                </div>
                <div className="inline-flex items-center justify-center h-6 w-6 bg-primary-600 rounded-full text-white text-xs font-medium mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-navy-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-navy-500 font-light leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Crew/Helpers */}
      <section className="py-24 bg-navy-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                Are You a Yacht Professional?
              </h2>
              <p className="mt-6 text-lg text-navy-400 font-light leading-relaxed">
                Join our network of trusted crew and helpers. Find yacht crew positions,
                offer mooring assistance, and connect with owners worldwide.
              </p>

              <ul className="mt-10 space-y-4">
                {[
                  'Free to join, build your professional profile',
                  'Find crew positions or offer mooring help',
                  'Build your reputation with verified reviews',
                  'Connect with yacht owners worldwide',
                ].map((item, index) => (
                  <li key={index} className="flex items-center text-navy-300 font-light">
                    <CheckCircle className="h-5 w-5 text-primary-400 mr-3 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>

              <Link
                to="/register?type=helper"
                className="inline-flex items-center mt-10 bg-white text-navy-900 hover:bg-navy-100 font-medium py-3 px-7 rounded-full transition-all duration-300"
              >
                Register as Crew/Helper
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>

            <div className="mt-12 lg:mt-0">
              <div className="rounded-apple-xl p-8"
                style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}
              >
                <div className="text-center">
                  <div className="text-4xl font-bold text-white mb-2">Find Your Next Role</div>
                  <div className="text-navy-400 font-light">Crew positions & mooring jobs</div>
                </div>
                <div className="mt-8 grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-white">Seasonal</div>
                    <div className="text-sm text-navy-400 font-light">Contracts available</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">Global</div>
                    <div className="text-sm text-navy-400 font-light">Opportunities</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 tracking-tight">
            Ready to Get Started?
          </h2>
          <p className="mt-4 text-lg text-navy-500 font-light">
            Join yacht owners and crew already using 1crew3.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="btn-primary text-base px-8 py-3.5"
            >
              Create Free Account
            </Link>
            <Link
              to="/login"
              className="btn-secondary text-base px-8 py-3.5"
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-100 py-10"
        style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <img src="/logo.png" alt="1crew3" className="h-9 w-9 rounded-lg" />
              <span className="ml-2.5 text-lg font-bold text-navy-700 tracking-tight">
                1crew3
              </span>
            </div>
            <div className="flex space-x-8 text-sm text-navy-500">
              <a href="/blog" className="hover:text-navy-900 transition-colors duration-300">Blog</a>
              <Link to="/terms" className="hover:text-navy-900 transition-colors duration-300">Terms</Link>
              <Link to="/privacy" className="hover:text-navy-900 transition-colors duration-300">Privacy</Link>
              <Link to="/help" className="hover:text-navy-900 transition-colors duration-300">Help</Link>
              <Link to="/contact" className="hover:text-navy-900 transition-colors duration-300">Contact</Link>
            </div>
          </div>
          <div className="mt-6 text-center text-navy-400 text-xs">
            &copy; 2026 1crew3. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
